import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { HashLoader } from "react-spinners";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import Pagination from "../../components/pagination/Pagination";
import { getUserLogin } from "../../api/interaction.api";
import { getUserDepositHistory, getUserActionHistory } from "../../api/transaction.api";
import { getUserRecentOrders } from "../../api/order.api";
import { paymentMethodText, productTypeText, statusText } from "../../utils";
import "./admin-user-history-list.css";

const LIMIT = 10;

const HISTORY_CONFIG = {
  login: {
    title: "Lịch sử đăng nhập",
    breadcrumb: "Đăng nhập",
    fetcher: (userId, page) => getUserLogin(userId, LIMIT, page),
    extract: (res) => ({
      rows: res?.data?.interactions || [],
      totalPages: Number(res?.data?.totalPages || 1),
    }),
    headers: ["STT", "Hành động", "Thời gian"],
    mapRow: (item, index, page) => [
      (page - 1) * LIMIT + index + 1,
      "Đăng nhập",
      item?.createdAt ? new Date(item.createdAt).toLocaleString("vi-VN") : "-",
    ],
    emptyText: "Không có dữ liệu đăng nhập",
  },
  deposits: {
    title: "Lịch sử nạp tiền",
    breadcrumb: "Nạp tiền",
    fetcher: (userId, page) => getUserDepositHistory(userId, LIMIT, page),
    extract: (res) => ({
      rows: res?.data?.deposit_history || [],
      totalPages: Number(res?.data?.totalPages || 1),
    }),
    headers: ["STT", "Mã giao dịch", "Số tiền", "Phương thức", "Trạng thái", "Thời gian"],
    mapRow: (item, index, page) => [
      (page - 1) * LIMIT + index + 1,
      item?.transactionId || "-",
      `${Number(item?.amount || 0).toLocaleString("vi-VN")}đ`,
      paymentMethodText(item?.paymentMethod) || "NA",
      statusText(item?.transactionStatus) || "-",
      item?.createdAt ? new Date(item.createdAt).toLocaleString("vi-VN") : "-",
    ],
    emptyText: "Không có dữ liệu nạp tiền",
  },
  actions: {
    title: "Lịch sử chỉnh sửa số dư",
    breadcrumb: "Chỉnh số dư",
    fetcher: (userId, page) => getUserActionHistory(userId, LIMIT, page),
    extract: (res) => ({
      rows: res?.data?.actions || [],
      totalPages: Number(res?.data?.totalPages || 1),
    }),
    headers: ["STT", "Số tiền", "Người", "Lý do", "Thời gian"],
    mapRow: (item, index, page) => [
      (page - 1) * LIMIT + index + 1,
      `${item?.transactionType === "add" ? "+" : "-"} ${Number(item?.amount || 0).toLocaleString("vi-VN")}đ`,
      item?.author || "-",
      item?.reason || "-",
      item?.createdAt ? new Date(item.createdAt).toLocaleString("vi-VN") : "-",
    ],
    emptyText: "Không có dữ liệu chỉnh sửa",
  },
  orders: {
    title: "Lịch sử mua hàng",
    breadcrumb: "Đơn hàng",
    fetcher: (userId, page) => getUserRecentOrders(userId, LIMIT, page),
    extract: (res) => ({
      rows: res?.data?.orders || [],
      totalPages: Number(res?.data?.totalPages || 1),
    }),
    headers: ["STT", "Mã đơn", "Giá", "Loại", "Trạng thái", "Thời gian"],
    mapRow: (item, index, page) => [
      (page - 1) * LIMIT + index + 1,
      item?.orderId || "-",
      `${Number(item?.order_final_amount || 0).toLocaleString("vi-VN")}đ`,
      productTypeText(item?.order_type),
      statusText(item?.order_status) || "-",
      item?.createdAt ? new Date(item.createdAt).toLocaleString("vi-VN") : "-",
    ],
    emptyText: "Không có dữ liệu đơn hàng",
  },
};

export const AdminUserHistoryList = () => {
  const { userId, historyType } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const config = useMemo(() => HISTORY_CONFIG[historyType], [historyType]);

  useEffect(() => {
    setPage(1);
  }, [historyType, userId]);

  useEffect(() => {
    fetchHistory();
  }, [historyType, userId, page]);

  const fetchHistory = async () => {
    if (!config) return;
    try {
      setLoading(true);
      const response = await config.fetcher(userId, page);
      const { rows: nextRows, totalPages: nextTotalPages } = config.extract(response);
      setRows(nextRows);
      setTotalPages(nextTotalPages || 1);
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!config) {
    return (
      <section className="auh-page">
        <p className="auh-not-found">Loại lịch sử không hợp lệ.</p>
      </section>
    );
  }

  return (
    <section className="auh-page">
      <ToastNotification />
      <div className="auh-shell">
        <header className="auh-head">
          <div>
            <p className="auh-breadcrumb">
              <Link to="/super-admin/users">Quản trị / Người dùng</Link> /{" "}
              <Link to={`/super-admin/users/edit/${userId}`}>Chỉnh sửa</Link> / {config.breadcrumb}
            </p>
            <h1 className="auh-title">{config.title}</h1>
          </div>
          <button className="auh-back-btn" type="button" onClick={() => navigate(`/super-admin/users/edit/${userId}`)}>
            Quay lại
          </button>
        </header>

        {loading ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" />
          </div>
        ) : (
          <>
            <div className="auh-table-wrap">
              <table className="auh-table">
                <thead>
                  <tr>
                    {config.headers.map((head) => (
                      <th key={head}>{head}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length > 0 ? (
                    rows.map((item, idx) => (
                      <tr key={`${historyType}-${idx}`}>
                        {config.mapRow(item, idx, page).map((cell, cellIdx) => (
                          <td key={`${idx}-${cellIdx}`}>{cell}</td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={config.headers.length} className="auh-empty-cell">
                        {config.emptyText}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(nextPage) => setPage(nextPage)}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default AdminUserHistoryList;
